import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import { UNASSIGNED_TEACHER_ID, type TReviewFacetOption, type TReviewFilterMode } from '@onesource/schemas';
import { getMediaUrl } from '@onesource/services';
import { FC, useMemo, useState } from 'react';
import { MdCheck, MdPersonOff, MdRemove, MdSearch } from 'react-icons/md';

import { FILTER_ROSE, FILTER_ROSE_DARK } from '../feed-filter-bar/filter-trigger-button';
import { ReviewFilterPanelSkeleton } from './review-filter-panel-skeleton';

/** Long lists get a search box; short ones are faster to scan. */
const SEARCH_FROM = 7;
const EXCLUDE_TONE = '#3d4a63';

export interface TReviewFilterValue {
  ids: string[];
  /** Labels of the picked ids, so the button can name them while the panel is closed. */
  labels: Record<string, string>;
  mode: TReviewFilterMode;
}

export const EMPTY_FILTER_VALUE: TReviewFilterValue = { ids: [], labels: {}, mode: 'include' };

interface ReviewFilterPanelProps {
  title: string;
  /** Plural noun for the helper line: "modules", "forms", "teachers". */
  noun: string;
  options: TReviewFacetOption[] | undefined;
  isLoading: boolean;
  value: TReviewFilterValue;
  onChange: (value: TReviewFilterValue) => void;
  onDone: () => void;
  showAvatars?: boolean;
}

/**
 * Pick several values and say whether to keep only them or everything but them.
 * Every value shows how many submissions it matches with the other filters as
 * they are, so the teacher sees the result before they click. Changes apply at
 * once; Done only closes.
 */
export const ReviewFilterPanel: FC<ReviewFilterPanelProps> = ({
  title,
  noun,
  options,
  isLoading,
  value,
  onChange,
  onDone,
  showAvatars = false,
}) => {
  const [query, setQuery] = useState('');
  const isExclude = value.mode === 'exclude';
  const tone = isExclude ? EXCLUDE_TONE : FILTER_ROSE;
  const selected = useMemo(() => new Set(value.ids), [value.ids]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!options || !needle) {
      return options ?? [];
    }
    return options.filter(o => `${o.label} ${o.hint ?? ''}`.toLowerCase().includes(needle));
  }, [options, query]);

  const toggle = (option: TReviewFacetOption) => {
    const isPicked = selected.has(option.id);
    const ids = isPicked ? value.ids.filter(id => id !== option.id) : [...value.ids, option.id];
    const labels = { ...value.labels };
    if (isPicked) {
      delete labels[option.id];
    } else {
      labels[option.id] = option.label;
    }
    onChange({ ...value, ids, labels });
  };

  const setMode = (mode: TReviewFilterMode) => onChange({ ...value, mode });

  const segment = (mode: TReviewFilterMode, label: string) => {
    const isOn = value.mode === mode;
    return (
      <ButtonBase
        role="radio"
        tabIndex={0}
        aria-checked={isOn}
        onClick={() => setMode(mode)}
        sx={{
          px: 1.25,
          height: 26,
          borderRadius: '8px',
          fontSize: 12,
          fontWeight: 700,
          color: isOn ? (mode === 'exclude' ? EXCLUDE_TONE : FILTER_ROSE_DARK) : 'text.secondary',
          bgcolor: isOn ? 'common.white' : 'transparent',
          boxShadow: isOn ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: FILTER_ROSE_DARK, outlineOffset: 1 },
          transition: 'background-color 0.15s ease, color 0.15s ease',
        }}
      >
        {label}
      </ButtonBase>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 'inherit' }}>
      <Box sx={{ px: 2, pt: 1.75, pb: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography sx={{ fontFamily: 'Figtree, sans-serif', fontWeight: 700, fontSize: 15 }}>{title}</Typography>
          <Box
            role="radiogroup"
            aria-label="Filter mode"
            sx={{ display: 'flex', p: '3px', borderRadius: '10px', bgcolor: '#f1eeed' }}
          >
            {segment('include', 'Include')}
            {segment('exclude', 'Exclude')}
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
          {isExclude ? `Show all ${noun} except the ones you pick.` : `Show only the ${noun} you pick.`}
        </Typography>
        {(options?.length ?? 0) >= SEARCH_FROM && (
          <Box
            sx={{
              mt: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.25,
              height: 36,
              borderRadius: '10px',
              bgcolor: '#f5f3f2',
              color: 'text.secondary',
            }}
          >
            <MdSearch size={18} />
            <InputBase
              autoFocus
              fullWidth
              placeholder={`Search ${noun}`}
              value={query}
              onChange={e => setQuery(e.target.value)}
              inputProps={{ 'aria-label': `Search ${noun}` }}
              sx={{ fontSize: 14 }}
            />
          </Box>
        )}
      </Box>

      <Box
        role="group"
        aria-label={title}
        sx={{ flex: 1, minHeight: 0, maxHeight: 320, overflowY: 'auto', px: 1, pb: 1, scrollbarWidth: 'thin' }}
      >
        {isLoading && !options ? (
          <ReviewFilterPanelSkeleton />
        ) : visible.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1.25, py: 2, textAlign: 'center' }}>
            {query ? `No ${noun} match “${query}”.` : `No ${noun} yet.`}
          </Typography>
        ) : (
          visible.map(option => {
            const isPicked = selected.has(option.id);
            return (
              <ButtonBase
                key={option.id}
                role="checkbox"
                tabIndex={0}
                aria-checked={isPicked}
                onClick={() => toggle(option)}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.25,
                  py: 1,
                  borderRadius: '10px',
                  textAlign: 'left',
                  bgcolor: isPicked ? (isExclude ? '#f3f4f7' : '#fbf3f5') : 'transparent',
                  '&:hover': { bgcolor: isPicked ? (isExclude ? '#eceef3' : '#f7e9ed') : '#f7f6f5' },
                  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: tone, outlineOffset: -2 },
                }}
              >
                {/* A check to keep, a dash to leave out — the box says what the pick does. */}
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 18,
                    height: 18,
                    borderRadius: '5px',
                    display: 'grid',
                    placeItems: 'center',
                    color: 'common.white',
                    border: '1.5px solid',
                    borderColor: isPicked ? tone : '#c9c4c2',
                    bgcolor: isPicked ? tone : 'common.white',
                    transition: 'background-color 0.12s ease, border-color 0.12s ease',
                  }}
                >
                  {isPicked && (isExclude ? <MdRemove size={14} /> : <MdCheck size={14} />)}
                </Box>
                {showAvatars &&
                  (option.id === UNASSIGNED_TEACHER_ID ? (
                    // "Nobody" gets an empty, dashed seat instead of a letter that reads like a person.
                    <Box
                      sx={{
                        flexShrink: 0,
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        border: '1.5px dashed #b9b2af',
                        display: 'grid',
                        placeItems: 'center',
                        color: '#8a8380',
                      }}
                    >
                      <MdPersonOff size={14} />
                    </Box>
                  ) : (
                    <Avatar
                      src={option.avatar ? getMediaUrl(option.avatar) : undefined}
                      sx={{ width: 26, height: 26, fontSize: 12 }}
                    >
                      {option.label.charAt(0).toUpperCase()}
                    </Avatar>
                  ))}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      fontWeight: isPicked ? 700 : 500,
                      color: option.count === 0 && !isPicked ? 'text.disabled' : 'text.primary',
                      textDecoration: isPicked && isExclude ? 'line-through' : 'none',
                    }}
                  >
                    {option.label}
                  </Typography>
                  {option.hint && (
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {option.hint}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    flexShrink: 0,
                    minWidth: 28,
                    textAlign: 'right',
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    color: option.count === 0 ? 'text.disabled' : 'text.secondary',
                  }}
                >
                  {option.count}
                </Typography>
              </ButtonBase>
            );
          })
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pt: 1.25,
          // Clear of the phone's home bar when the panel is a bottom sheet.
          pb: 'calc(10px + env(safe-area-inset-bottom))',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          size="small"
          variant="text"
          disabled={value.ids.length === 0}
          onClick={() => onChange({ ...value, ids: [], labels: {} })}
          sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
        >
          Clear
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={onDone}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', boxShadow: 'none', px: 2.5 }}
        >
          Done
        </Button>
      </Box>
    </Box>
  );
};
